import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getCloseReview,
  getCloseReviewHistory,
  reopenCloseReview,
  reopenPeriodLock,
  trueUpCloseReview,
  type CloseReview,
  type CloseReviewDetail,
  type CloseReviewPosting,
} from "@/lib/api";
import { useOrganization } from "@/lib/organization";
import { CultivationCapabilityNotice } from "./CultivationCapabilityNotice";
import { capabilityAllowed } from "./cultivation-capabilities";
import { timestampLabel } from "./cultivation-dates";
import { CultivationPack } from "./CultivationPack";
import { humanizeFieldName } from "./cultivation-review";
import type { CultivationWorkspace } from "./cultivation-types";

function reviewTone(state: string): "success" | "danger" | "neutral" | "warning" {
  if (state === "posted" || state === "locked") return "success";
  if (state === "failed" || state === "correction_required") return "danger";
  if (state === "posting") return "warning";
  return "neutral";
}

function postingTone(status: string): "success" | "danger" | "neutral" | "warning" {
  if (status === "posted" || status === "applied") return "success";
  if (status === "failed" || status === "rejected") return "danger";
  if (status === "pending" || status === "running") return "warning";
  return "neutral";
}

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function dayAfter(value: string): string {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return isoDay(date);
}

function dayBefore(value: string): string {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return isoDay(date);
}

function monthEnd(value: string): string {
  const date = new Date(`${value}T00:00:00Z`);
  return isoDay(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)));
}

function reviewLabel(review: CloseReview): string {
  return `${review.period_start}–${review.period_end} · ${review.state.replaceAll("_", " ")}`;
}

export function CultivationHistory({
  workspace,
  onOpenReview,
  onOpenPeriodReview,
}: {
  workspace: CultivationWorkspace;
  onOpenReview: () => void;
  onOpenPeriodReview?: (start: string, end: string) => void;
}) {
  const { scope } = useOrganization();
  const [history, setHistory] = useState<CloseReview[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [detail, setDetail] = useState<CloseReviewDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reversal, setReversal] = useState<CloseReviewPosting | null>(null);
  const [reversalDate, setReversalDate] = useState(workspace.periodEnd);
  const [reversalReason, setReversalReason] = useState("");
  const [trueUp, setTrueUp] = useState<CloseReview | null>(null);
  const [trueUpStart, setTrueUpStart] = useState("");
  const [trueUpEnd, setTrueUpEnd] = useState("");
  const [trueUpReason, setTrueUpReason] = useState("");
  const [trueUpApproval, setTrueUpApproval] = useState("");
  const [reopen, setReopen] = useState<CloseReview | null>(null);
  const [reopenReason, setReopenReason] = useState("");
  const [reopenLockDate, setReopenLockDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canView = capabilityAllowed(workspace.capabilities, "view");
  const canCorrect = capabilityAllowed(workspace.capabilities, "correct");
  const canLock = capabilityAllowed(workspace.capabilities, "lock");
  const rowBusy = busy || historyLoading || detailLoading;
  const historyToken = useRef(0);
  const detailToken = useRef(0);
  const scopeKey = [scope?.legal_entity_id ?? "", workspace.licenseId].join("|");
  const scopeKeyRef = useRef(scopeKey);
  scopeKeyRef.current = scopeKey;

  const reloadHistory = useCallback(async () => {
    const capturedScope = scopeKey;
    const token = ++historyToken.current;
    if (!canView || !scope?.legal_entity_id || !workspace.licenseId) {
      setHistory([]);
      setNextCursor(null);
      setHistoryLoading(false);
      return;
    }
    setHistory([]);
    setNextCursor(null);
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const result = await getCloseReviewHistory({
        legal_entity_id: scope.legal_entity_id,
        license_id: workspace.licenseId,
        limit: 50,
      });
      if (token !== historyToken.current || scopeKeyRef.current !== capturedScope) return;
      setHistory(result.reviews);
      setNextCursor(result.next_cursor ?? null);
    } catch (cause) {
      if (token !== historyToken.current || scopeKeyRef.current !== capturedScope) return;
      setHistoryError(cause instanceof Error ? cause.message : "Close history is unavailable.");
    } finally {
      if (token === historyToken.current && scopeKeyRef.current === capturedScope) {
        setHistoryLoading(false);
      }
    }
  }, [canView, scope?.legal_entity_id, scopeKey, workspace.licenseId]);

  const loadMoreHistory = useCallback(async () => {
    if (!canView || !scope?.legal_entity_id || !workspace.licenseId || !nextCursor) return;
    const capturedScope = scopeKey;
    const token = ++historyToken.current;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const result = await getCloseReviewHistory({
        legal_entity_id: scope.legal_entity_id,
        license_id: workspace.licenseId,
        limit: 50,
        cursor: nextCursor,
      });
      if (token !== historyToken.current || scopeKeyRef.current !== capturedScope) return;
      setHistory((current) => [...current, ...result.reviews]);
      setNextCursor(result.next_cursor ?? null);
    } catch (cause) {
      if (token !== historyToken.current || scopeKeyRef.current !== capturedScope) return;
      setHistoryError(cause instanceof Error ? cause.message : "Close history is unavailable.");
    } finally {
      if (token === historyToken.current && scopeKeyRef.current === capturedScope) {
        setHistoryLoading(false);
      }
    }
  }, [canView, nextCursor, scope?.legal_entity_id, scopeKey, workspace.licenseId]);

  useEffect(() => {
    historyToken.current += 1;
    detailToken.current += 1;
    setHistory([]);
    setNextCursor(null);
    setHistoryError(null);
    setDetail(null);
    setError(null);
    setNotice(null);
  }, [scopeKey]);

  useEffect(() => {
    void reloadHistory();
  }, [reloadHistory]);

  async function openDetailById(reviewId: string) {
    const token = ++detailToken.current;
    const capturedScope = scopeKeyRef.current;
    setDetailLoading(true);
    setError(null);
    try {
      const loaded = await getCloseReview(reviewId);
      if (token === detailToken.current && scopeKeyRef.current === capturedScope) {
        setDetail(loaded);
      }
    } catch (cause) {
      if (token === detailToken.current && scopeKeyRef.current === capturedScope) {
        setError(cause instanceof Error ? cause.message : "The review detail is unavailable.");
      }
    } finally {
      if (token === detailToken.current && scopeKeyRef.current === capturedScope) {
        setDetailLoading(false);
      }
    }
  }

  function openDetail(review: CloseReview) {
    void openDetailById(review.review_id);
  }

  async function confirmReversal() {
    if (!reversal) return;
    if (!reversalDate || !reversalReason.trim()) {
      setError("A reversal date and reason are required.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await workspace.reversePosting(reversal, reversalReason.trim(), reversalDate);
      setReversal(null);
      setReversalReason("");
      setNotice("The posting was reversed. Review the corrected inputs, then post again.");
      await reloadHistory();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The posting could not be reversed.");
    } finally {
      setBusy(false);
    }
  }

  function openTrueUp(review: CloseReview) {
    const start = dayAfter(review.period_end);
    setTrueUp(review);
    setTrueUpStart(start);
    setTrueUpEnd(monthEnd(start));
    setTrueUpReason("");
    setTrueUpApproval("");
    setError(null);
    setNotice(null);
  }

  async function confirmTrueUp() {
    if (!trueUp) return;
    if (!trueUpStart || !trueUpEnd || !trueUpReason.trim()) {
      setError("A target period and true-up reason are required.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await trueUpCloseReview(trueUp.review_id, {
        target_period_start: trueUpStart,
        target_period_end: trueUpEnd,
        reason: trueUpReason.trim(),
        ...(trueUpApproval.trim() ? { approval_ref: trueUpApproval.trim() } : {}),
      });
      setNotice(`True-up review ${result.review.review_id} created for ${result.review.period_start}–${result.review.period_end}.`);
      setTrueUp(null);
      await reloadHistory();
      onOpenPeriodReview?.(result.review.period_start, result.review.period_end);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The true-up could not be created.");
    } finally {
      setBusy(false);
    }
  }

  function openReopen(review: CloseReview) {
    setReopen(review);
    setReopenReason("");
    setReopenLockDate(dayBefore(review.period_start));
    setError(null);
    setNotice(null);
  }

  async function confirmReopen() {
    if (!reopen || !scope?.legal_entity_id) return;
    if (!reopenReason.trim() || !reopenLockDate) {
      setError("A reopen reason and new lock date are required.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    let reviewReopened = false;
    try {
      // The review must be marked correction_required before any lock move.
      // The review reopen route never moves the lock itself.
      await reopenCloseReview(reopen.review_id, { reason: reopenReason.trim() });
      reviewReopened = true;
      const lock = await reopenPeriodLock({
        legal_entity_id: scope.legal_entity_id,
        new_lock_date: reopenLockDate,
        reason: reopenReason.trim(),
      });
      setNotice(`Period lock reopened to ${lock.lock_date ?? reopenLockDate}. The review is marked correction required.`);
      setReopen(null);
      await Promise.allSettled([reloadHistory(), workspace.reloadReviews()]);
      onOpenPeriodReview?.(reopen.period_start, reopen.period_end);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "The period could not be reopened.";
      if (reviewReopened) {
        await Promise.allSettled([reloadHistory(), workspace.reloadReviews()]);
        setError(
          `The review is marked correction required, but the period lock was not moved. ` +
          `Retry the lock move from Settings → Books → Period locks, or try this reopen again. ${message}`,
        );
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  }

  if (!canView) {
    return <CultivationCapabilityNotice capability="view" action="open the cultivation history" />;
  }

  if (!workspace.licenseId) {
    return (
      <EmptyState
        title="Choose a location"
        reason="History is scoped to one location and close period. Choose a location above to load saved reviews."
      />
    );
  }

  return (
    <div className="grid gap-5">
      <section className="surface grid gap-3 rounded-2xl border border-[var(--border)] p-5">
        <div className="flex flex-wrap items-center gap-2">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Close history</h2>
            <p className="mt-1 text-sm text-[var(--muted-foreground)]">
              Saved, posted, corrected, and locked reviews across periods for this location. Current period {workspace.periodStart}–{workspace.periodEnd}.
            </p>
          </div>
          <Button className="ms-auto" type="button" variant="secondary" onClick={onOpenReview}>
            Open close review
          </Button>
        </div>

        {historyError ? <p role="alert" className="text-sm text-[var(--danger)]">{historyError}</p> : null}
        {historyLoading ? <p role="status" className="text-sm text-[var(--muted-foreground)]">Loading cross-period history…</p> : null}
        {!historyLoading && history.length === 0 ? (
          <EmptyState
            title="No saved review"
            reason="Nothing has been saved for this location. Start the close review to create the first version."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Period</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">State</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Revision</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Approval</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Reviewed</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Posted</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Lineage</th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {history.map((review) => (
                  <tr key={review.review_id}>
                    <td className="px-2 py-1">{review.period_start}–{review.period_end}</td>
                    <td className="px-2 py-1"><Badge tone={reviewTone(review.state)}>{review.state.replaceAll("_", " ")}</Badge></td>
                    <td className="px-2 py-1">{review.review_revision ?? 1}</td>
                    <td className="px-2 py-1">
                      <Badge tone={review.approval_state === "approved" ? "success" : review.approval_state === "rejected" ? "danger" : review.approval_required ? "warning" : "neutral"}>
                        {(review.approval_state ?? "not_required").replaceAll("_", " ")}
                      </Badge>
                    </td>
                    <td className="px-2 py-1">{timestampLabel(review.reviewed_at)}</td>
                    <td className="px-2 py-1">{timestampLabel(review.posted_at)}</td>
                    <td className="px-2 py-1">
                      {review.correction_kind ? (
                        <div className="grid gap-1 text-xs">
                          <Badge tone="warning">{review.correction_kind.replaceAll("_", " ")}</Badge>
                          {review.correction_of_review_id ? (
                            <button
                              type="button"
                              className="text-left text-[var(--action)] underline-offset-4 hover:underline"
                              disabled={rowBusy}
                              onClick={() => {
                                const original = history.find((item) => item.review_id === review.correction_of_review_id);
                                if (original) {
                                  openDetail(original);
                                } else if (review.correction_of_review_id) {
                                  void openDetailById(review.correction_of_review_id);
                                }
                              }}
                            >
                              Original review
                            </button>
                          ) : null}
                          {review.correction_reason ? <span className="text-[var(--muted-foreground)]">{review.correction_reason}</span> : null}
                        </div>
                      ) : (
                        <div className="grid gap-1 text-xs">
                          {history.filter((item) => item.correction_of_review_id === review.review_id).map((child) => (
                            <button
                              key={child.review_id}
                              type="button"
                              className="text-left text-[var(--action)] underline-offset-4 hover:underline"
                              disabled={rowBusy}
                              onClick={() => {
                                onOpenPeriodReview?.(child.period_start, child.period_end);
                              }}
                            >
                              {child.correction_kind ?? "correction"} · {child.period_start}
                            </button>
                          ))}
                          {review.reopened_at ? <span className="text-[var(--muted-foreground)]">Reopened {timestampLabel(review.reopened_at)}</span> : null}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex flex-wrap gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`View review for ${review.period_start} to ${review.period_end}`}
                          onClick={() => void openDetail(review)}
                          disabled={rowBusy}
                        >
                          {review.period_start === workspace.periodStart && review.period_end === workspace.periodEnd ? "Open saved review" : "View"}
                        </Button>
                        {["posted", "locked", "correction_required"].includes(review.state) ? (
                          <>
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              aria-label={`Create true-up for ${review.period_start} to ${review.period_end}`}
                              onClick={() => openTrueUp(review)}
                              disabled={!canCorrect || rowBusy}
                            >
                              True-up
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              aria-label={`Reopen review for ${review.period_start} to ${review.period_end}`}
                              onClick={() => openReopen(review)}
                              disabled={!canCorrect || !canLock || rowBusy}
                            >
                              Reopen
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {nextCursor ? (
          <Button type="button" variant="secondary" onClick={() => void loadMoreHistory()} disabled={historyLoading}>
            {historyLoading ? "Loading…" : "Load older reviews"}
          </Button>
        ) : null}
        {!canCorrect ? <CultivationCapabilityNotice capability="correct" action="true-up or reopen a posted close" /> : null}
        {!canLock ? <CultivationCapabilityNotice capability="lock" action="reopen the period lock" /> : null}
      </section>

      <CultivationPack workspace={workspace} showSchedules={false} />

      <section className="surface grid gap-3 rounded-2xl border border-[var(--border)] p-5">
        <h2 className="text-sm font-semibold">Current-period postings</h2>
        {workspace.postings.length === 0 ? (
          <p className="text-sm text-[var(--muted-foreground)]">No journals have been posted for this period.</p>
        ) : (
          <ul className="grid gap-2">
            {workspace.postings.map((posting) => (
              <li key={posting.posting_id} className="grid gap-2 rounded-xl border border-[var(--border)] p-3 sm:grid-cols-[1fr_auto] sm:items-center">
                <div className="grid gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{posting.family}</span>
                    <Badge tone={postingTone(posting.status)}>{posting.status}</Badge>
                    <span className="text-xs text-[var(--muted-foreground)]">{posting.entry_date}</span>
                  </div>
                  <p className="text-sm text-[var(--muted-foreground)]">{posting.memo}</p>
                  {posting.reversal_journal_entry_id ? (
                    <Link
                      to="/journal/$entryId"
                      params={{ entryId: posting.reversal_journal_entry_id }}
                      className="text-sm text-[var(--action)] underline-offset-4 hover:underline"
                    >
                      Open reversal journal
                    </Link>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  {posting.journal_entry_id ? (
                    <Button asChild variant="secondary" size="sm">
                      <Link to="/journal/$entryId" params={{ entryId: posting.journal_entry_id }}>Open journal</Link>
                    </Button>
                  ) : null}
                  {(posting.status === "posted" || posting.status === "applied") ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!canCorrect || busy}
                      onClick={() => {
                        setReversal(posting);
                        setReversalDate(workspace.periodEnd);
                        setReversalReason("");
                        setError(null);
                        setNotice(null);
                      }}
                    >
                      Reverse
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
        {!canCorrect ? <CultivationCapabilityNotice capability="correct" action="reverse a posted close" /> : null}
      </section>

      {error ? <p role="alert" className="text-sm text-[var(--danger)]">{error}</p> : null}
      {notice ? <p role="status" className="text-sm text-[var(--muted-foreground)]">{notice}</p> : null}

      <Dialog
        open={Boolean(detail)}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
        title="Close review detail"
        description={detail ? reviewLabel(detail.review) : undefined}
      >
        {detail ? (
          <div className="grid gap-3 text-sm">
            <dl className="grid gap-1">
              <div className="flex justify-between gap-3"><dt className="font-medium">Reviewer</dt><dd>{detail.review.reviewed_by_user_id ? detail.review.reviewed_by_user_id.slice(0, 8) : "unknown"}</dd></div>
              <div className="flex justify-between gap-3"><dt className="font-medium">Reviewed</dt><dd>{timestampLabel(detail.review.reviewed_at)}</dd></div>
              <div className="flex justify-between gap-3"><dt className="font-medium">Input fingerprint</dt><dd className="font-mono text-xs">{detail.review.input_sha256 ? `${detail.review.input_sha256.slice(0, 16)}…` : "—"}</dd></div>
              <div className="flex justify-between gap-3"><dt className="font-medium">Approval</dt><dd>{(detail.review.approval_state ?? "not_required").replaceAll("_", " ")}{detail.review.approval_note ? ` · ${detail.review.approval_note}` : ""}</dd></div>
              {detail.review.correction_kind ? (
                <div className="flex justify-between gap-3"><dt className="font-medium">Correction</dt><dd>{detail.review.correction_kind.replaceAll("_", " ")} · {detail.review.correction_reason ?? "no reason"}</dd></div>
              ) : null}
            </dl>
            {Object.keys(detail.form_values).length > 0 ? (
              <div className="max-h-72 overflow-auto rounded-lg border border-[var(--border)]">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="border-b border-[var(--border)] px-2 py-1 text-left">Field</th>
                      <th className="border-b border-[var(--border)] px-2 py-1 text-left">Saved value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(detail.form_values).sort(([left], [right]) => left.localeCompare(right)).map(([key, value]) => (
                      <tr key={key}>
                        <td className="px-2 py-1">{humanizeFieldName(key)}</td>
                        <td className="px-2 py-1 font-mono text-xs">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-[var(--muted-foreground)]">No form values were saved with this review.</p>}
          </div>
        ) : null}
      </Dialog>

      <Dialog
        open={Boolean(trueUp)}
        onOpenChange={(open) => {
          if (!open && !busy) setTrueUp(null);
        }}
        title="Create a true-up review"
        description={trueUp ? `Link a correction to the original ${trueUp.period_start}–${trueUp.period_end} review. A new review is created for an open later period.` : undefined}
      >
        <div className="grid gap-3 text-sm">
          <label className="grid gap-1 text-xs font-medium">
            Target period start
            <Input type="date" value={trueUpStart} onChange={(event) => setTrueUpStart(event.target.value)} disabled={busy} />
          </label>
          <label className="grid gap-1 text-xs font-medium">
            Target period end
            <Input type="date" value={trueUpEnd} min={trueUpStart} onChange={(event) => setTrueUpEnd(event.target.value)} disabled={busy} />
          </label>
          <label className="grid gap-1 text-xs font-medium">
            Reason
            <textarea
              className="min-h-24 w-full rounded-md border border-[var(--border)] bg-[var(--surface)] p-3 text-sm"
              value={trueUpReason}
              onChange={(event) => setTrueUpReason(event.target.value)}
              placeholder="Explain why the original close needs a true-up"
              disabled={busy}
            />
          </label>
          <label className="grid gap-1 text-xs font-medium">
            Approval reference (optional)
            <Input value={trueUpApproval} onChange={(event) => setTrueUpApproval(event.target.value)} disabled={busy} />
          </label>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setTrueUp(null)} disabled={busy}>Cancel</Button>
            <Button type="button" onClick={() => void confirmTrueUp()} disabled={busy}>
              {busy ? "Creating…" : "Create true-up"}
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={Boolean(reopen)}
        onOpenChange={(open) => {
          if (!open && !busy) setReopen(null);
        }}
        title="Reopen the period lock"
        description={reopen ? `Mark the ${reopen.period_start}–${reopen.period_end} review as correction required, then move the period lock backward through the dedicated period-lock reopen endpoint.` : undefined}
      >
        <div className="grid gap-3 text-sm">
          <label className="grid gap-1 text-xs font-medium">
            New lock date
            <Input type="date" value={reopenLockDate} onChange={(event) => setReopenLockDate(event.target.value)} disabled={busy} />
          </label>
          <label className="grid gap-1 text-xs font-medium">
            Reason
            <textarea
              className="min-h-24 w-full rounded-md border border-[var(--border)] bg-[var(--surface)] p-3 text-sm"
              value={reopenReason}
              onChange={(event) => setReopenReason(event.target.value)}
              placeholder="Explain why the locked period must be reopened"
              disabled={busy}
            />
          </label>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setReopen(null)} disabled={busy}>Cancel</Button>
            <Button type="button" variant="destructive" onClick={() => void confirmReopen()} disabled={busy}>
              {busy ? "Reopening…" : "Reopen period"}
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={Boolean(reversal)}
        onOpenChange={(open) => {
          if (!open) setReversal(null);
        }}
        title="Reverse posted journal"
        description="The reversal creates a linked journal entry. It does not erase the original posting."
      >
        <div className="grid gap-3">
          <label className="grid gap-1 text-sm">
            Reversal date
            <Input type="date" value={reversalDate} onChange={(event) => setReversalDate(event.target.value)} />
          </label>
          <label className="grid gap-1 text-sm">
            Reason
            <textarea
              className="min-h-24 w-full rounded-md border border-[var(--border)] bg-[var(--surface)] p-3 text-sm"
              value={reversalReason}
              onChange={(event) => setReversalReason(event.target.value)}
              placeholder="Explain why the posted journal is being reversed"
            />
          </label>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setReversal(null)} disabled={busy}>Cancel</Button>
            <Button type="button" variant="destructive" onClick={() => void confirmReversal()} disabled={busy}>
              {busy ? "Reversing…" : "Reverse posting"}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
